import { useMemo, useState } from 'react';
import { SortValue } from '../components/SortableHeader';
import ISorter from '../ISorter';

// eslint-disable-next-line max-len
export const sortItems = <T, TSortProps>(items: readonly T[], sorter: ISorter<T, TSortProps>, sort?: SortValue<TSortProps>): T[] => {
  if (!sort) return [...items];

  return [...items].sort((a, b) => {
    const compare = sorter.sort(a, b, sort.type, sort.fallback);
    return sort.asc ? compare : compare * -1;
  });
};

/**
 * A hook that allows sorting of items.
 * @param items The array of items.
 * @param sorter The sorter that performs the comparisons.
 * @param initialSort The initial sorting parameters.
 * @returns The resulting sorted items, the current sorting parameters and a function to update it.
 */
// eslint-disable-next-line max-len
export const useSorting = <T, TSortProps>(items: readonly T[], sorter: ISorter<T, TSortProps>, initialSort?: SortValue<TSortProps>): [
  T[],
  SortValue<TSortProps> | undefined,
  (sort?: SortValue<TSortProps> | undefined) => void,
] => {
  const [sort, setSort] = useState<SortValue<TSortProps> | undefined>(initialSort);

  const resultItems = useMemo(() => sortItems(items, sorter, sort), [items, sorter, sort]);
  return [resultItems, sort, setSort];
};

export default useSorting;
